import type { Annotation } from '../schema';

export interface AnnotationRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Renders the four overlay annotation kinds (highlight / underline / encircle /
 * annotate) on top of the element layer. `boxes` maps element id → bounding
 * rect (measured by SlideStage). zoom/spotlight are handled by SlideStage.
 */
export function AnnotationOverlay({
  annotations,
  boxes,
  revealedElementIds,
}: {
  annotations: Annotation[];
  boxes: Record<string, AnnotationRect>;
  revealedElementIds: Set<string> | null;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {annotations.map((a) => {
        const rect = boxes[a.targetElementId];
        if (!rect) return null;
        if (revealedElementIds && !revealedElementIds.has(a.targetElementId)) return null;
        return <AnnotationMark key={a.id} annotation={a} rect={rect} />;
      })}
    </div>
  );
}

// Styling mirrors the admin teach Stage's AnnotationMark (teach is the source
// of truth). Pure inline styles — no reliance on host Tailwind scanning.
function AnnotationMark({ annotation, rect }: { annotation: Annotation; rect: AnnotationRect }) {
  const color = annotation.color ?? '#facc15';

  if (annotation.kind === 'highlight') {
    return (
      <div
        style={{
          position: 'absolute',
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          background: color,
          opacity: 0.35,
          borderRadius: 6,
          mixBlendMode: 'multiply',
        }}
      />
    );
  }
  if (annotation.kind === 'underline') {
    return (
      <div
        style={{
          position: 'absolute',
          left: rect.left + 4,
          top: rect.top + rect.height - 2,
          width: rect.width - 8,
          height: 3,
          background: color,
          borderRadius: 2,
        }}
      />
    );
  }
  if (annotation.kind === 'encircle') {
    return (
      <div
        style={{
          position: 'absolute',
          left: rect.left - 8,
          top: rect.top - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          border: `3px dashed ${color}`,
          borderRadius: 9999,
        }}
      />
    );
  }
  if (annotation.kind === 'annotate') {
    return (
      <div
        style={{
          position: 'absolute',
          left: rect.left + rect.width + 12,
          top: rect.top,
          maxWidth: 220,
        }}
      >
        <div
          style={{
            background: color,
            color: '#ffffff',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 11,
            fontWeight: 500,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
          }}
        >
          {annotation.text || 'Note'}
        </div>
      </div>
    );
  }
  return null;
}
